import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Grid, Typography, Button, Card, CardContent, Avatar, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';


export default function SEOSection() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, []);

  const features = [
    {
      title: "Technical SEO",
      description: "Optimize your website's code, speed, and mobile usability for better search engine performance.",
      icon: "⚙️",
      likes: "2.9K",
      comments: "198"
    },
    {
      title: "Content Optimization",
      description: "Write keyword-rich, valuable content that ranks and converts.",
      icon: "📝",
      likes: "3.1K",
      comments: "234"
    },
    {
      title: "Local SEO",
      description: "Rank higher for local searches and attract nearby customers to your business.",
      icon: "📍",
      likes: "2.7K",
      comments: "187"
    },
    {
      title: "Link Building",
      description: "Earn high-quality backlinks that increase authority and trust with search engines.",
      icon: "🔗",
      likes: "2.8K",
      comments: "212"
    }
  ];

  const benefits = [
    "Higher Google rankings",
    "More targeted traffic",
    "Better website performance",
    "Enhanced user experience",
    "Greater ROI",
    "Sustainable business growth"
  ];

  return (
    <>

<Box
  sx={{
    position: 'fixed',
    top: { xs: 50, sm: 70 },
    left: { xs: 12, sm: 24 },
    zIndex: 1200,
    bgcolor: 'white',
    borderRadius: '50%',
    boxShadow:5,
    width: { xs: 40, sm: 48 },
    height: { xs: 40, sm: 48 },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  <IconButton
    onClick={() => navigate(-1)}
    size="medium"
    sx={{
      color: '#1877f2',
      p: 0.5
    }}
  >
    <ArrowBackIosNewIcon fontSize="small" />
  </IconButton>
</Box>

      <Helmet>
        <title>SEO Services in Visakhapatnam | SLGT Digital</title>
        <meta name="description" content="Get top-notch SEO services in Visakhapatnam to rank your website higher on Google. Improve traffic, engagement, and sales with SLGT Digital's proven strategies." />
        <meta name="keywords" content="Best digital marketing,Best digital marketing in anakapalli,SEO Visakhapatnam, SEO Agency Vizag, SLGT Digital SEO, Search Engine Optimization Vizag, Local SEO Visakhapatnam" />
        <link rel="canonical" href="https://www.slgt.in/services/seo" />
      </Helmet>


      <Box sx={{ pt: { xs: 8, sm: 10 }, pb: { xs: 6, sm: 8 }, minHeight: '100vh', bgcolor: '#f0f2f5' }}>
        {/* Hero Section */}
        <Box sx={{ bgcolor: '#ffffff', color: '#1c1e21', py: { xs: 6, md: 8 }, mb: { xs: 4, md: 6 }, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: '#1877f2', fontFamily: 'Nunito, serif' }}>SEO</Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1c1e21', fontFamily: 'Nunito, serif' }}>Search Engine Optimization</Typography>
                    <Typography sx={{ color: '#65676b', fontSize: '0.9rem' }}>Boost visibility with expert SEO strategies</Typography>
                  </Box>
                </Box>
                <Typography variant="h1" sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }, fontWeight: 700, mb: 3, color: '#1c1e21', lineHeight: 1.2 }}>
                  Get Found. Get Clicks. Grow Faster.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 4, color: '#65676b', lineHeight: 1.6 }}>
                  Our SEO services in Visakhapatnam help your business rank higher on search engines, attract quality traffic, and generate more leads. SLGT Digital offers white-hat, data-driven SEO that delivers measurable results.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/requestquotes')}
                  sx={{ bgcolor: '#1877f2', color: 'white', '&:hover': { bgcolor: '#166fe5' }, px: 6, py: 2, fontSize: '1.1rem', fontWeight: 600, borderRadius: '6px', textTransform: 'none' }}
                >
                  Request a Quote
                </Button>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                <img
                  src="/assets/services/se2.png"
                  alt="SEO Services in Visakhapatnam"
                  style={{ width: '100%', maxWidth: '500px', height: 'auto', margin: '0 auto', borderRadius: '8px' }}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Features and Benefits Section */}
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, fontWeight: 700, mb: 6, textAlign: 'center', color: '#1c1e21' }}>
            What Our SEO Services Include
          </Typography>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={{ height: '100%', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', bgcolor: '#ffffff' }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e4e6eb' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: '#1877f2' }}>{feature.icon}</Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 600, color: '#1c1e21' }}>{feature.title}</Typography>
                          <Typography sx={{ fontSize: '0.9rem', color: '#65676b' }}>Included in all SEO plans</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Typography sx={{ color: '#1c1e21', fontSize: '1rem', lineHeight: 1.6 }}>{feature.description}</Typography>
                    </Box>
                    <Box sx={{ p: 1, borderTop: '1px solid #e4e6eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small" sx={{ color: '#1877f2' }}><FavoriteIcon /></IconButton>
                        <Typography sx={{ color: '#65676b', fontSize: '0.9rem' }}>{feature.likes}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small" sx={{ color: '#65676b' }}><CommentIcon /></IconButton>
                        <Typography sx={{ color: '#65676b', fontSize: '0.9rem' }}>{feature.comments}</Typography>
                      </Box>
                      <IconButton size="small" sx={{ color: '#65676b' }}><ShareIcon /></IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h2" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, fontWeight: 700, mt: 10, mb: 6, textAlign: 'center', color: '#1c1e21' }}>
            Why Choose SLGT Digital for SEO?
          </Typography>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', bgcolor: '#ffffff' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: '#1877f2' }}>✓</Avatar>
                      <Typography sx={{ fontSize: '1rem', color: '#1c1e21', fontWeight: 500 }}>{benefit}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* CTA Section */}
        <Container maxWidth="lg" sx={{ mt: 8, textAlign: 'center' }}>
          <Card sx={{ borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', bgcolor: '#ffffff', p: { xs: 3, md: 4 } }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, fontWeight: 700, mb: 3, color: '#1c1e21' }}>
              Let’s Elevate Your Search Visibility
            </Typography>
            <Typography sx={{ mb: 4, fontSize: { xs: '1rem', sm: '1.1rem' }, maxWidth: '800px', mx: 'auto', color: '#65676b', lineHeight: 1.6 }}>
              Schedule a free consultation and discover how our expert SEO services can increase traffic, leads, and ROI.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/requestquotes')}
              sx={{ bgcolor: '#1877f2', color: 'white', '&:hover': { bgcolor: '#166fe5' }, px: 6, py: 2, fontSize: '1.1rem', fontWeight: 600, borderRadius: '6px', textTransform: 'none' }}
            >
              Get My Free SEO Audit
            </Button>
          </Card>
        </Container>
      </Box>
    </>
  );
}
